import { Keyboard, RotateCcw, ScanLine, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { codigoDoTexto } from "./api";
import ConferirSaida from "./ConferirSaida";
import { criarLeitor } from "./lib/lerQr";

// Leitor de QR de saída dentro do sistema (telas de Mesas e do Garçom — dono,
// 24/09/2026). Liga a câmera traseira, lê o QR do comprovante do cliente e
// abre a conferência ali mesmo. Sem câmera (ou sem permissão), dá pra digitar
// o código que aparece embaixo do link.
export default function LerQrSaida({ aberto, onFechar }) {
  const video = useRef(null);
  const [codigo, setCodigo] = useState(null);
  const [erro, setErro] = useState(null);
  const [digitado, setDigitado] = useState("");
  const [manual, setManual] = useState(false);

  // Fechou: a próxima abertura começa do zero (não mostra a pessoa anterior
  // com o LIBERAR na tela)
  useEffect(() => {
    if (aberto) return;
    setCodigo(null);
    setErro(null);
    setDigitado("");
    setManual(false);
  }, [aberto]);

  useEffect(() => {
    if (!aberto || codigo || manual) return undefined;
    let stream = null;
    let vivo = true;
    let espera = null;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
        if (!vivo) {
          // Fechado (ou trocado para digitar) antes da câmera responder: desliga
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        video.current.srcObject = stream;
        await video.current.play();
        const ler = await criarLeitor();
        const passo = async () => {
          if (!vivo) return;
          try {
            const texto = await ler(video.current);
            const achado = codigoDoTexto(texto);
            if (achado) {
              setCodigo(achado);
              return;
            }
          } catch {
            /* quadro ruim: tenta o próximo */
          }
          espera = setTimeout(passo, 250);
        };
        passo();
      } catch {
        if (!vivo) return;
        setErro("Não consegui abrir a câmera. Libere a câmera no navegador ou digite o código.");
        setManual(true);
      }
    })();
    return () => {
      vivo = false;
      clearTimeout(espera);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [aberto, codigo, manual]);

  // Esc fecha (no computador do caixa, sem precisar do mouse)
  useEffect(() => {
    if (!aberto) return undefined;
    const tecla = (e) => { if (e.key === "Escape") onFechar(); };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [aberto, onFechar]);

  if (!aberto) return null;

  const outro = () => { setCodigo(null); setErro(null); setDigitado(""); };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-e2e="ler-qr-saida">
      <div className="w-full sm:max-w-md bg-background rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <ScanLine className="w-5 h-5 text-primary" />
          <h2 className="font-black">Conferir saída</h2>
          <button type="button" onClick={onFechar} className="ml-auto p-2 -mr-2" aria-label="Fechar"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {codigo ? (
            <>
              <ConferirSaida codigo={codigo} />
              <button type="button" onClick={outro} className="w-full h-11 rounded-xl border font-bold flex items-center justify-center gap-1.5">
                <RotateCcw className="w-4 h-4" /> Ler outro QR
              </button>
            </>
          ) : manual ? (
            <div className="space-y-2">
              {erro ? <p className="text-sm text-amber-600">{erro}</p> : null}
              <input value={digitado} onChange={(e) => setDigitado(e.target.value)} placeholder="Código ou link do comprovante"
                data-e2e="ler-qr-codigo" className="w-full h-12 rounded-xl border px-3 bg-background" />
              <button type="button" onClick={() => setCodigo(codigoDoTexto(digitado))} disabled={!codigoDoTexto(digitado)}
                className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-black disabled:opacity-50">CONFERIR</button>
            </div>
          ) : (
            <>
              <div className="relative rounded-2xl overflow-hidden bg-black aspect-square">
                <video ref={video} playsInline muted className="w-full h-full object-cover" />
                <div className="absolute inset-10 border-4 border-white/80 rounded-2xl pointer-events-none" />
              </div>
              <p className="text-sm text-muted-foreground text-center">Aponte para o QR do comprovante do cliente.</p>
              <button type="button" onClick={() => setManual(true)} className="w-full h-11 rounded-xl border font-bold flex items-center justify-center gap-1.5">
                <Keyboard className="w-4 h-4" /> Digitar o código
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
